/**
 * @author NikolaV
 */
import { LayerWrapper } from '../LayerWrapper';
import { Ajax } from '@shared/Ajax';
import Feature from 'ol/Feature';
import WebGLVectorLayerRenderer from 'ol/renderer/webgl/VectorLayer.js';
import Layer from 'ol/layer/Layer';
import WebGLPointsLayer from 'ol/layer/WebGLPoints';
import VectorSource from 'ol/source/Vector';
import { PipeFeatureFactory } from '@openlayers/pipes/PipeFeatureFactory';
import { removeFeaturesBySource } from '../ol-utils';


class WebGLLayer extends Layer {
    createRenderer() {
        return new WebGLVectorLayerRenderer(this, {
            style: {
                'stroke-color': ['*', ['get', 'COLOR'], [0, 0, 0]],
                'stroke-width': [
                    "match", ["get", "hover"], 'true',
                    4,
                    2
                ],
            }
        });
    }
}

export class StringlinesLayer extends LayerWrapper<IOutlinePolyline> {
    featureType: FeatureType = 'stringline';
    overlayModelFeaturesById: { [key: string]: Feature[] } = {};

    arrowsLayer: WebGLPointsLayer<any> = new WebGLPointsLayer({
        source: new VectorSource() as any,
        style: {
            "shape-points": 3,
            "shape-radius": 5,
            "shape-displacement": [0, -8],
            "shape-rotation": ['get', 'rotation', 'number'],
            "shape-fill-color": 'black',
            "shape-rotate-with-view": true
        }
    });
    arrowsLayerAdded = false;

    constructor(opts) {
        //opts.layerBuilderFn = () => new WebGLLayer({}) // works but no hit detection for now
        super(opts);

        const arrowsSource = (this.arrowsLayer.getSource() as VectorSource);
        const addOrRemoveDeviationsLayer = _.debounce(() => {
            if (arrowsSource.isEmpty()) {
                this.removeArrowsLayer();
            } else {
                this.addArrowsLayer();
            }
        }, 10);
        arrowsSource.on('addfeature', addOrRemoveDeviationsLayer);
        arrowsSource.on('removefeature', addOrRemoveDeviationsLayer);
    }

    drawAllFromOutlineLayers(layers: IOutlineLayer[], filename?: string) {
        const stringlines: IOutlinePolyline[] = _.flatten(
            layers.map(layer => // take only non-solid stringlines from each layer
                layer.polylines
                     .filter(pl => !pl.solid)
                     .map(stringline => ({
                         ...stringline,
                         modelId: layer.id,
                         filename // cant use layer.name, its not always equal to filename
                     }))
            ),
        );

        const featuresResult = this.drawAll(stringlines);
        featuresResult.features.forEach(f => {
            if (f.get('showElevationArrow')) {
                this.arrowsLayer.getSource().addFeatures(PipeFeatureFactory.makeArrowFeatures(f, 'end'));
            }
        })

        return stringlines;
    }

    removeFeaturesBySource(source: FeatureSource, silent: boolean = true) {
        removeFeaturesBySource(this.arrowsLayer.getSource() as VectorSource, source, false);
        super.removeFeaturesBySource(source, silent);
    }

    removeOverlayModel = (modelId: number) => {
        for (let i = 0, ilen = this.overlayModelFeaturesById[modelId].length; i < ilen; i++) {
            const feature: Feature = this.overlayModelFeaturesById[modelId][i];
            this.layerSource.removeFeature(feature);
        }
        delete this.overlayModelFeaturesById[modelId];
    }

    toggleOverlayModel = _.debounce((modelId: number) => {
        if (this.overlayModelFeaturesById[modelId]) {
            this.removeOverlayModel(modelId);
        } else {
            if ($.isNumeric(modelId)) {
                Ajax({
                    url: `/api/1/file/${modelId}/stringlines.json`
                }).then((resp: IStringlinesAjaxResponse) => {
                    const stringlines = this.drawAll(resp.stringlines)?.features;
                    const success = stringlines;
                    this.overlayModelFeaturesById[modelId] = stringlines;
                    return success ? $.Deferred().resolve(resp) : $.Deferred().reject(resp);
                }, resp =>
                    $.Deferred().reject(resp, status, 'ajax error'));
            }
        }
    }, 50);

    getOverlayModelIds = (): number[] => {
        return Object.keys(this.overlayModelFeaturesById).map(parseFloat);
    }

    addArrowsLayer = () => {
        if (this.arrowsLayerAdded || this.arrowsLayer.getSource().isEmpty()) return;

        this.arrowsLayerAdded = true;
        this.map.addLayer(this.arrowsLayer);
        this.arrowsLayer.setZIndex(this.layer.getZIndex()-1);
    }

    removeArrowsLayer = () => {
        this.arrowsLayerAdded = false;
        this.map.removeLayer(this.arrowsLayer);
    }
}
